import {Injectable} from '@angular/core';
import {Dialogs} from "@ionic-native/dialogs";
import {DirectoryEntry, Entry, File, FileEntry} from "@ionic-native/file";
import {FileTransfer} from "@ionic-native/file-transfer";
import {InAppBrowser} from "@ionic-native/in-app-browser";
import {ThemeableBrowser} from "@ionic-native/themeable-browser";
import {App, DeepLinker, Platform} from "ionic-angular";
import {ServiceConfig} from "../app/services-config";

declare const cordova: any;
declare const pdf: any;

const appUrlRegex = new RegExp(`^${ServiceConfig.baseUrl}`);
const urlRegex = /^https?:\/\//i;
const pdfRegex = /\.pdf(\?|$)/i;
const androidBrowserRegex = /\.(jpg|bmp|gif|png)(\?|$)/i;
const iosBrowserRegex = /\.(pdf|(doc|xls|ppt)x?|(key|pages|numbers)(\.zip)?|rtf(d\.zip)?)(\?|$)/i;
const nonUrlRegex = /^(https?|file|data|blob):/;
const noSchemaRegex = /^[a-z]+:/;

const browserConfig: any = {
  statusbar: {
    color: `${ServiceConfig.headerBgColor}ff`
  },
  toolbar: {
    height: 44,
    color: `${ServiceConfig.headerBgColor}ff`
  },
  title: {
    color: `${ServiceConfig.headerFgColor}ff`,
    showPageTitle: true
  },
  closeButton: {
    image: 'close',
    imagePressed: 'close_pressed',
    align: 'right',
    event: 'closePressed'
  },
  location: true,
  hidden: false,
  clearcache: false,
  clearsessioncache: false,
  zoom: true,
  backButtonCanClose: true,
  disableAnimation: false,
  fullscreen: false
};

@Injectable()
export class UrlOpenerProvider {
  private isCordova = this.platform.is('cordova');
  private isAndroid = this.isCordova && this.platform.is('android');
  private isIos = this.isCordova && !this.isAndroid && this.platform.is('ios');
  private hasInAppBrowser = this.isCordova && !!cordova.InAppBrowser;
  private hasPdfReader = !!(<any>window).pdf;
  private hasThemeableBrowser = this.isCordova && (this.isAndroid || this.isIos) && !!cordova.ThemeableBrowser;

  constructor(private app: App,
              private deepLinker: DeepLinker,
              private platform: Platform,
              private dialogs: Dialogs,
              private file: File,
              private fileTransfer: FileTransfer,
              private inAppBrowser: InAppBrowser,
              private themeableBrowser: ThemeableBrowser) {
  }

  openFile(file: string, title?: string) {
    if (file) {
      file = file.replace(/^\/+/, '').replace(/\/+$/, '');

      if (this.isCordova) {
        return this.openUrl(file, title);
      }

      return this.openRelativeUrl(file, title);
    }

    return Promise.reject('missing file');
  }

  openRelativeUrl(url: string, title?: string) {
    if (url) {
      return this.openUrl(`${ServiceConfig.baseUrl}/${url}`, title);
    }

    return Promise.reject('missing url');
  }

  openUrl(url?: string, title?: string): Promise<any> {
    if (url) {
      if (appUrlRegex.test(url)) {
        const targetPage = url.replace(appUrlRegex, '');

        if (targetPage.indexOf('/uploads') !== 0) {
          const segments = this.deepLinker.getCurrentSegments(targetPage);

          if (segments.length) {
            return this.app.getRootNavs()[0].push(segments[0].name, segments[0].data);
          }
        }
      }

      title = title || decodeURI(url.split('/').pop().split('.').shift());
      url = encodeURI(url);

      if (this.isCordova) {
        if ((this.isIos || this.isAndroid) && pdfRegex.test(url) && this.hasPdfReader) {
          if (urlRegex.test(url)) {
            return this.downloadFile(url).then((entry) => this.showPdf(entry, title));
          }

          const sourceFile = `${this.file.applicationDirectory.replace(/\/+$/, '')}/www/${url}`;
          return this.copyFile(sourceFile, this.file.dataDirectory).then((entry: FileEntry) => this.showPdf(entry, title));
        }

        if (this.hasThemeableBrowser && androidBrowserRegex.test(url) || (this.isIos && iosBrowserRegex.test(url))) {
          if (!nonUrlRegex.test(url)) {
            return this.file.resolveLocalFilesystemUrl(`${this.file.applicationDirectory}www/${url}`)
              .then((entry) => this.showInternalBrowser(entry.toURL()));
          }

          return this.showInternalBrowser(url);
        }
      }

      if (!noSchemaRegex.test(url)) {
        return this.openRelativeUrl(url);
      }

      if (this.hasInAppBrowser) {
        this.inAppBrowser.create(url, '_system');
      } else {
        window.open(url, '_blank');
      }

      return Promise.resolve();
    }

    return Promise.reject('missing url');
  }

  private downloadFile(url: string): Promise<FileEntry> {
    const fileTransfer = this.fileTransfer.create();
    const target = `${this.file.dataDirectory.replace(/\/+$/, '')}/file.pdf`;

    return fileTransfer.download(url, target);
  }

  private resolveFile(file: string): Promise<Entry> {
    return this.file.resolveLocalFilesystemUrl(file);
  }

  private copyWrapper(source: FileEntry, target: DirectoryEntry, newName?: string) {
    return new Promise((resolve, reject) => source.copyTo(target, newName, resolve, reject));
  }

  private copyFile(source: string, target: string) {
    return this.resolveFile(source).then(
      (sourceEntry: FileEntry) => this.resolveFile(target).then(
        (targetEntry: DirectoryEntry) => this.copyWrapper(sourceEntry, targetEntry)
      )
    );
  }

  private showPdf(entry: FileEntry, title: string) {
    return new Promise((resolve, reject) => {
      pdf.openPdf(
        entry.toURL(),
        title,
        {
          annotationsEnabled: false,
          shareEnabled: false,
          isAnnotatedPdf: false,
          headerColor: ServiceConfig.headerBgColor.replace('#', '#ff'),
          headerTint: ServiceConfig.headerFgColor.replace('#', '#ff')
        },
        () => entry.remove(resolve, reject),
        (error) => entry.remove(() => reject(error), reject)
      );
    });
  }

  private internalBrowserError(message: string): Promise<any> {
    return this.dialogs.confirm(message, "Waarschuwing", ["OK", "Annuleren"])
      .then(button => {
        if (button === 1) {
          return Promise.resolve();
        }

        return Promise.reject(false);
      })
  }

  private showInternalBrowser(url: string): Promise<any> {
    let browser;

    const promise = new Promise((resolve, reject) => {
      const ThemeableBrowser = cordova.ThemeableBrowser;

      browser = this.themeableBrowser.create(url, '_blank', browserConfig);

      let loaded = false;
      const browserErrorSubscription = browser.on(ThemeableBrowser.EVT_ERR).subscribe(error => {
        if (error.code === ThemeableBrowser.ERR_CRICTICAL) {
          console.error(`ThemeableBrowser critical: ${error.message}`);
        } else if (error.code === ThemeableBrowser.ERR_LOADFAIL) {
          console.error(`ThemeableBrowser loadfail: ${error.message}`);
        } else {
          console.error(`ThemeableBrowser (${error.code}): ${error.message}`);
        }

        browser.hide();
        reject(error);
      });

      const browserWarningSubscription = browser.on(ThemeableBrowser.EVT_WRN).subscribe(error => {
        if (error.code === ThemeableBrowser.WRN_UNDEFINED) {
          console.warn(`ThemeableBrowser undefined: ${error.message}`);
        } else if (error.code === ThemeableBrowser.WRN_UNEXPECTED) {
          console.warn(`ThemeableBrowser unexpected: ${error.message}`);
        } else {
          console.warn(`ThemeableBrowser (${error.code}): ${error.message}`);
        }
      });

      let browserLoadStopSubscription = browser.on('loadstop').subscribe(() => {
        browserLoadStopSubscription.unsubscribe();
        loaded = true;
        browserLoadStopSubscription = null;
      });

      const browserLoadErrorSubscription = browser.on('loaderror').subscribe(error => {
        if (this.isIos) {
          if ([102, 204, -999].indexOf(error.code) !== -1) {
            return;
          }

          browser.hide();

          if ([100, -1002].indexOf(error.code)) {
            this.themeableBrowser.create(error.url, '_system', {});
            browser.close();
            return;
          }

          let message;
          if ([-1005, -1009, -1020].indexOf(error.code)) {
            message = 'Het laden van het document is mislukt omdat u (tijdelijk) geen netwerkverbinding heeft, klik op OK om het opnieuw te proberen.';
          } else if (error.code === -1018) {
            message = 'Kan het document niet laden omdat u roaming uitgeschakeld heeft, klik op OK om het opnieuw te proberen.';
          } else if (error.code === -1019) {
            message = 'Het laden van het document is gestopt omdat er een telefoongesprek bezig was, klik op OK om het document opnieuw te laden.';
          }

          if (message) {
            this.internalBrowserError(message).then(() => {
              browser.show();
              browser.reload();
            }, () => {
              reject(error);
            });
            return;
          }
        }

        console.error(`ThemeableBrowser loaderror (${error.code}): ${error.message}`);
        if (!loaded) {
          reject(error);
        }
      });

      const browserExitSubscription = browser.on('exit').subscribe(() => {
        browserErrorSubscription.unsubscribe();
        browserWarningSubscription.unsubscribe();
        browserExitSubscription.unsubscribe();
        browserLoadErrorSubscription.unsubscribe();

        if (browserLoadStopSubscription) {
          browserLoadStopSubscription.unsubscribe();
        }

        browser = null;

        resolve();
      });
    });

    promise.finally(() => {
        if (browser) {
          browser.hide();
          setTimeout(() => {
            browser.close();
          }, 500);
        }
    });

    return promise;
  }
}



// WEBPACK FOOTER //
// ./src/services/url-opener.service.ts